import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, setBasicAuth } from '@/lib/api';
import { cacheClear } from '@/lib/db';
import type { TraccarUser, TraccarServer } from '@/types';

type UserRole = 'admin' | 'manager' | 'driver';

interface SessionContextValue {
  user: TraccarUser | null;
  server: TraccarServer | null;
  ready: boolean;
  error: Error | null;
  role: UserRole;
  login: (email: string, password: string, code?: string) => Promise<TraccarUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (requiredRole: UserRole) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

type NativeAppWindow = Window & {
  appInterface?: {
    postMessage?: (message: string) => void;
  };
  webkit?: {
    messageHandlers?: {
      appInterface?: {
        postMessage?: (message: string) => void;
      };
    };
  };
  updateNotificationToken?: (token: string) => void;
};

function getNativeWindow(): NativeAppWindow {
  return window as NativeAppWindow;
}

function hasNativeAppBridge(): boolean {
  const nativeWindow = getNativeWindow();
  return Boolean(
    nativeWindow.appInterface
    || nativeWindow.webkit?.messageHandlers?.appInterface,
  );
}

function postNativeAppMessage(message: string): void {
  const nativeWindow = getNativeWindow();

  if (nativeWindow.webkit?.messageHandlers?.appInterface?.postMessage) {
    nativeWindow.webkit.messageHandlers.appInterface.postMessage(message);
  } else {
    nativeWindow.appInterface?.postMessage?.(message);
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<TraccarUser | null>(null);
  const [server, setServer] = useState<TraccarServer | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const nativeLoginSentFor = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const current = await api.session.get() as unknown;
      if (current && typeof current === 'object' && !Array.isArray(current) && (current as TraccarUser).id != null) {
        setUser(current as TraccarUser);
        setError(null);
      } else {
        setUser(null);
      }
    } catch (err) {
      const apiErr = err as { status?: number };
      if (apiErr.status !== 401 && apiErr.status !== 404) setError(err as Error);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const svr = await api.session.server() as unknown;
        if (svr && typeof svr === 'object' && !Array.isArray(svr)) {
          setServer(svr as TraccarServer);
        }
      } catch {
        /* server info is optional */
      }
      await refresh();
    })();
  }, [refresh]);

  useEffect(() => {
    const nativeWindow = getNativeWindow();

    if (!user) {
      nativeLoginSentFor.current = null;
      return undefined;
    }

    const handleNotificationToken = async (token: string) => {
      if (!token) return;

      window.localStorage.setItem('notificationToken', token);

      const currentTokens = typeof user.attributes?.notificationTokens === 'string'
        ? user.attributes.notificationTokens.split(',').filter(Boolean)
        : [];

      if (currentTokens.includes(token)) return;

      const updatedUser: TraccarUser = {
        ...user,
        attributes: {
          ...(user.attributes || {}),
          notificationTokens: [...currentTokens.slice(-2), token].join(','),
        },
      };

      try {
        const saved = await api.users.update(user.id, updatedUser) as TraccarUser;
        setUser(saved);
      } catch (err) {
        console.error('Failed to register Traccar Manager notification token', err);
      }
    };

    nativeWindow.updateNotificationToken = handleNotificationToken;

    if (
      hasNativeAppBridge()
      && nativeLoginSentFor.current !== user.id
    ) {
      nativeLoginSentFor.current = user.id;

      void (async () => {
        let token = '';

        try {
          const expiration = new Date();
          expiration.setMonth(expiration.getMonth() + 6);

          token = await api.session.generateToken(
            expiration.toISOString(),
          ) as unknown as string;
        } catch {
          token = '';
        }

        postNativeAppMessage(`login|${token}`);
      })();
    }

    return () => {
      if (nativeWindow.updateNotificationToken === handleNotificationToken) {
        delete nativeWindow.updateNotificationToken;
      }
    };
  }, [user]);

  const login = useCallback(async (email: string, password: string, code?: string) => {
    // Clear cached data from previous session to avoid serving stale data
    cacheClear().catch(() => {});
    const u = await api.session.login(email, password, code) as TraccarUser;
    // Store Basic auth for cross-origin API requests (bypasses SameSite cookie + WWW-Authenticate popup)
    setBasicAuth({ email, password });
    setUser(u);
    setError(null);
    try {
      setServer(await api.session.server() as TraccarServer);
    } catch {
      /* optional */
    }
    return u;
  }, []);

  const logout = useCallback(async () => {
    const notificationToken = window.localStorage.getItem('notificationToken');

    if (notificationToken && user) {
      window.localStorage.removeItem('notificationToken');

      const currentTokens = typeof user.attributes?.notificationTokens === 'string'
        ? user.attributes.notificationTokens.split(',').filter(Boolean)
        : [];

      if (currentTokens.includes(notificationToken)) {
        const remainingTokens = currentTokens.filter(
          (token) => token !== notificationToken,
        );

        const updatedUser: TraccarUser = {
          ...user,
          attributes: {
            ...(user.attributes || {}),
            notificationTokens: remainingTokens.length > 0
              ? remainingTokens.join(',')
              : undefined,
          },
        };

        try {
          await api.users.update(user.id, updatedUser);
        } catch {
          /* logout must continue even if token cleanup fails */
        }
      }
    }

    try {
      await api.session.logout();
    } catch {
      /* ignore */
    }

    if (hasNativeAppBridge()) {
      postNativeAppMessage('logout');
    }

    setBasicAuth(null);
    setUser(null);
    // Clear IndexedDB cache to prevent stale data from previous session
    cacheClear().catch(() => {});
  }, [user]);

  const role: UserRole = user?.administrator ? 'admin' : user?.userLimit ? 'manager' : 'driver';

  const can = useCallback(
    (requiredRole: UserRole): boolean => {
      if (!user) return false;
      if (role === 'admin') return true;
      return role === requiredRole;
    },
    [role, user],
  );

  return (
    <SessionContext.Provider
      value={{ user, server, ready, error, role, login, logout, refresh, can }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = (): SessionContextValue => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
};
